import { useEffect, useState } from 'react';
import { Check, Moon, Monitor, Sun } from 'lucide-react';
import { clsx } from 'clsx';
import { Tooltip } from '../ui/Tooltip';
import { useTheme, type ThemePref } from '../../lib/theme';

const options: { value: ThemePref; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Terang', icon: Sun },
  { value: 'dark', label: 'Gelap', icon: Moon },
  { value: 'system', label: 'Sistem', icon: Monitor },
];

export function ThemeSwitch() {
  const { pref, effective, setPref } = useTheme();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const CurrentIcon = effective === 'dark' ? Moon : Sun;

  return (
    <div className="relative">
      <Tooltip label={`Tema · ${options.find((o) => o.value === pref)?.label}`}>
        <button
          type="button"
          aria-label="Ganti tema"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="grid size-9 place-items-center rounded-none text-muted transition-colors hover:bg-surface-2/70 press"
        >
          <CurrentIcon className="size-[19px]" />
        </button>
      </Tooltip>

      {open && (
        <>
          <button
            type="button"
            aria-label="Tutup menu tema"
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            aria-label="Tema"
            className="glass absolute right-0 top-full z-50 mt-2 w-44 rounded-none border border-hairline p-1.5 shadow-float"
          >
            {options.map((o) => {
              const active = o.value === pref;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => {
                    setPref(o.value);
                    setOpen(false);
                  }}
                  className={clsx(
                    'flex w-full items-center gap-2.5 rounded-none px-2.5 py-2 text-[13px] font-semibold transition-colors press',
                    active
                      ? 'bg-accent-soft text-accent-strong'
                      : 'text-muted hover:bg-surface-2/70 hover:text-ink',
                  )}
                >
                  <o.icon className="size-4" />
                  <span className="flex-1 text-left">{o.label}</span>
                  {active && <Check className="size-4" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
